import React, { useState } from "react";
import { Row, Col, Card, Form, Button } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import Layout from "../../layout/fullpage";
import { FaUserAlt, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";

const AuthLoginPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ email_or_phone: "", password: "" });
  const [validated, setValidated] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirectUrl') || null;

  const handleInputs = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
    setLoginError(false); // Reset error when user types
  };

  const checkUserOrders = async (userId) => {
    const authToken = localStorage.getItem("authToken");
    try {
      const response = await fetch(`${process.env.REACT_APP_HOST}api/orders/userOrders?user_id=${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await response.json();

      if (data.success && Array.isArray(data.data) && data.data.length > 0) {
        // User has orders - navigate to profile page
        navigate("/shop/auths/user-profile-page");
        return true;
      } else {
        // User has no orders - show alert and navigate to shop
        Swal.fire({
          imageUrl: "/shop/images/Ordernotfound.svg",
          imageWidth: '100%',
          imageHeight: 'auto',
          imageAlt: "Order Not Found",
          title: 'No Orders Found',
          text: 'You need to complete at least one order to access this feature.',
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false
        }).then(() => {
          navigate("/shop");
        });
        return false;
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong. Please try again.",
      });
      // Navigate to shop on error
      navigate("/shop");
      return false;
    }
  };

  const generateOTP = (event) => {
    event.preventDefault(); // Prevent default always first

    const form = event.currentTarget;
    const isFormValid = form.checkValidity();

    if (!isFormValid) {
      setValidated(true); // Only show validation if form is invalid
      event.stopPropagation();
      return;
    }

    axios
      .post(`${process.env.REACT_APP_HOST}api/login`, data, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
      })
      .then((response) => {
        if (response.data.success) {
          setValidated(true); // Show green checkmarks only on success
          setLoginError(false); // Clear any previous errors
          const { token, id, name, user_type, user_details } = response.data.data;

          localStorage.setItem("authToken", token);
          localStorage.setItem("user_id", id);
          localStorage.setItem("user_name", name);
          localStorage.setItem("user_type", user_type);

          Swal.fire({
            imageUrl: "/shop/images/Welcome.svg",
            imageWidth: '100%',
            imageHeight: 'auto',
            imageAlt: "Success Image",
            title: "Success!",
            text: "Welcome to Elite World",
            timer: 2000,
            timerProgressBar: true,
            showConfirmButton: false,
            customClass: {
              image: 'full-size-image',
            },
          }).then(() => {
            if (redirectUrl) {
              navigate(`/${redirectUrl}`);
            } else {
              // Check if user has orders
              checkUserOrders(id);
            }
          });

        } else {
          setValidated(false); // Don't set validated for errors
          setLoginError(true); // Set error state for red styling
          Swal.fire({
            imageUrl: "/shop/images/Failed_1.svg",
            imageWidth: '100%',
            imageHeight: 'auto',
            imageAlt: "Error Image",
            title: "Login Failed",
            text: "Incorrect username or password",
            timer: 3000, // Auto-close after 3 seconds
            timerProgressBar: true,
            showConfirmButton: false,
          });

        }
      })
      .catch((err) => {
        setValidated(false); // Don't set validated for errors
        setLoginError(true); // Set error state for red styling
        Swal.fire({
          imageUrl: "/shop/images/Failed_1.svg",
          imageWidth: '100%',
          imageHeight: 'auto',
          imageAlt: "Error Image",
          title: "Login Failed",
          text: "Incorrect username or password",
          timer: 3000, // Auto-close after 3 seconds
          timerProgressBar: true,
          showConfirmButton: false,
        });
      });
  };

  return (
    <Layout title="Login" centered>
      <div className="container p-2 p-sm-6 mb-mobile-40">
        <Card className="overflow-hidden card-gutter-lg rounded-4 card-auth card-auth-mh">
          <Row className="g-0 flex-column flex-lg-row">
            {/* Image Section - 35% height on mobile */}
            <Col lg="7" className="d-block p-0 mobile-image-container">
              <div className="text-center">
                <img
                  src="/shop/images/elite-main1.png"
                  className="custom-image mt-3"
                  alt="Centered logo"
                  style={{ width: "30%", backgroundColor: "rgb(255, 255, 255)", marginBottom: "10px" }}
                />
              </div>
              <Card.Body
                className="h-100 d-flex flex-column justify-content-center p-0 custom-bg12"
                style={{
                  backgroundImage: "url('/shop/images/Login.svg')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  minHeight: "35vh", // 35% of viewport height on mobile
                  marginTop: "-80px",
                }}
              />
            </Col>

            {/* Form Section - 55% height on mobile */}
            <Col lg="5" className="login-mobile-center mobile-form-container">
              <Card.Body className="h-100 d-flex flex-column justify-content-center">
                {/* <div className="text-center">
                  <img
                    src="/shop/images/elite-main1.png"
                    className="custom-image"
                    alt="Centered logo"
                    style={{ width: "30%", backgroundColor: "rgb(255, 255, 255)", marginBottom: "10px" }}
                  />
                </div> */}
                {/* <div className="nk-block-head text-center">
                  <h3 className="nk-block-title mb-1">Login</h3>
                  <p className="small">Please sign in to your account.</p>
                </div> */}
                <Form noValidate validated={validated} onSubmit={generateOTP}>
                  <Row className="gy-3">
                    <Col className="col-12">
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="email_or_phone" className="fw-semibold">
                          Enter Phone Or Email
                        </Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type="text"
                            id="email_or_phone"
                            name="email_or_phone"
                            value={data.email_or_phone}
                            onChange={handleInputs}
                            placeholder="Enter Phone Email"
                            className="ps-5 py-2 rounded-pill"
                            isInvalid={loginError}
                            isValid={validated && !loginError && data.email_or_phone}
                            required
                          />
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {loginError ? "Incorrect username or password" : "Username is required"}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col className="col-12">
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="password" className="fw-semibold">
                          Enter Password
                        </Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={data.password}
                            onChange={handleInputs}
                            placeholder="Enter password"
                            className="ps-5 rounded-pill"
                            isInvalid={loginError}
                            isValid={validated && !loginError && data.password}
                            required
                          />
                          <span
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: "absolute",
                              right: "15px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              cursor: "pointer",
                              color: "#6c757d"
                            }}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </span>
                        </div>
                        <Form.Control.Feedback type="invalid">
                          {loginError ? "Incorrect username or password" : "Password is required"}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col className="col-12">
                      <div className="d-flex flex-wrap justify-content-between">
                        <Form.Check
                          className="form-check-sm"
                          type="checkbox"
                          id="rememberMe"
                          label="Remember Me"
                        />
                        <Link to="/shop/auths/sms-verify" className="small">Forgot Password?</Link>
                      </div>
                    </Col>
                    <Col className="col-12">
                      <div className="d-grid">
                        <Button type="submit" variant="primary">
                          Login to account
                        </Button>
                      </div>
                      <div className="mt-3 text-center">
                        <Link
                          to="/shop/auths/user-register"
                          className="text-decoration-none text-secondary"
                        >
                          Don't have an account?{" "}
                          <strong className="text-primary">Register Here.</strong>
                        </Link>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Col>
          </Row>
        </Card>
      </div>

      {/* Add custom CSS for mobile view */}
      <style jsx>{`
        /* Hide browser's default password reveal button */
        input::-ms-reveal,
        input::-ms-clear {
          display: none;
        }
        input::-webkit-credentials-auto-fill-button {
          display: none !important;
        }
        @media (max-width: 991.98px) {
          .mobile-image-container {
            height: 45vh !important;
          }
          .mobile-form-container {
            height: 55vh !important;
            overflow-y: auto;
          }
          .card-auth-mh {
            min-height: 90vh !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default AuthLoginPage;